// src/pages/SchoolHomeSliderManager.js
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaArrowLeft,
  FaAward,
  FaCalendarAlt,
  FaCheck,
  FaEdit,
  FaExternalLinkAlt,
  FaGlobe,
  FaImage,
  FaInfoCircle,
  FaPlus,
  FaSave,
  FaSlidersH,
  FaSpinner,
  FaTimes,
  FaTrash,
  FaTrophy,
  FaUpload,
} from "react-icons/fa";
import { toast } from "react-toastify";
import api from "../api/httpClient";
import { API_PATHS } from "../api/apiPaths";
import { useTenant } from "../context/TenantContext";
import { useAppTheme } from "../context/AppThemeContext";
import Seo from "../components/Seo";
import {
  DEFAULT_PORTAL_CATEGORIES,
  loadTenantPortalContent,
  saveTenantPortalContent,
} from "../utils/tenantPortalContent";

const allowedTypes = new Set(["image/jpeg", "image/png", "image/webp"]);

const unwrap = (response) => {
  const value = response?.data?.data ?? response?.data ?? [];
  return Array.isArray(value) ? value : value.items || value.slides || [];
};

export default function SchoolHomeSliderManager() {
  const { activeTenant, subdomain, schoolName: contextSchoolName } = useTenant();
  let isDarkMode = false;
  try {
    const theme = useAppTheme();
    isDarkMode = Boolean(theme?.isDarkMode);
  } catch {
    isDarkMode = false;
  }

  const tenantKey = subdomain || activeTenant?.id || "default";
  const schoolName =
    contextSchoolName || activeTenant?.schoolName || activeTenant?.name || "School Campus";

  const [activeTab, setActiveTab] = useState("sliders"); // 'sliders' | 'achievements' | 'events' | 'identity'

  // Sliders state
  const [slides, setSlides] = useState([]);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [slideTitle, setSlideTitle] = useState("");
  const [slideCaption, setSlideCaption] = useState("");

  // Portal customizable content state
  const [portalContent, setPortalContent] = useState(() =>
    loadTenantPortalContent(tenantKey, schoolName)
  );

  // New Achievement Form State
  const [showAddAchievement, setShowAddAchievement] = useState(false);
  const [achForm, setAchForm] = useState({
    title: "",
    category: "STEM & Innovation",
    year: String(new Date().getFullYear()),
    statNumber: "",
    statLabel: "",
    description: "",
  });

  // New Event Form State
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [eventForm, setEventForm] = useState({
    title: "",
    category: "Academic",
    date: new Date().toISOString().slice(0, 10),
    time: "09:00 AM – 02:00 PM",
    venue: "Main Campus Auditorium",
    badge: "Upcoming",
    summary: "",
  });

  const loadSlides = useCallback(() => {
    return api
      .get(API_PATHS.branding.homeSlides())
      .then((response) => setSlides(unwrap(response)))
      .catch(() => setSlides([]));
  }, []);

  useEffect(() => {
    loadSlides();
  }, [loadSlides]);

  const chooseFiles = (event) => {
    const selected = [...(event.target.files || [])];
    event.target.value = "";
    if (
      selected.some(
        (file) => !allowedTypes.has(file.type) || file.size > 5 * 1024 * 1024
      )
    ) {
      return toast.error("Use JPEG, PNG, or WebP images up to 5 MB each.");
    }
    setFiles(selected.slice(0, 6));
  };

  const uploadSlides = async () => {
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const body = new FormData();
        body.append("file", file);
        if (slideTitle.trim()) body.append("title", slideTitle.trim());
        if (slideCaption.trim()) body.append("caption", slideCaption.trim());
        await api.post(API_PATHS.admin.homeSlides(), body);
      }
      setFiles([]);
      setSlideTitle("");
      setSlideCaption("");
      await loadSlides();
      window.dispatchEvent(new CustomEvent("school-home-slides:updated"));
      toast.success("School homepage banner uploaded successfully.");
    } catch (error) {
      toast.error(error?.message || "Unable to upload slider images.");
    } finally {
      setUploading(false);
    }
  };

  const removeSlide = async (slide) => {
    const id = slide.id || slide.slideId;
    if (!window.confirm("Remove this slider banner image?")) return;
    try {
      await api.delete(API_PATHS.admin.homeSlideById(id));
      setSlides((current) =>
        current.filter((item) => (item.id || item.slideId) !== id)
      );
      window.dispatchEvent(new CustomEvent("school-home-slides:updated"));
      toast.success("Slider banner removed.");
    } catch (error) {
      toast.error(error?.message || "Unable to remove slider image.");
    }
  };

  // Save full portal customizations
  const handleSavePortalContent = (updatedData = portalContent) => {
    const success = saveTenantPortalContent(tenantKey, updatedData);
    if (success) {
      setPortalContent(updatedData);
      toast.success("School website customizations saved.");
    } else {
      toast.error("Failed to save changes. Please try again.");
    }
  };

  // Achievement handlers
  const handleAddAchievement = (e) => {
    e.preventDefault();
    if (!achForm.title.trim() || !achForm.description.trim()) {
      return toast.warning("Please provide a title and description for the achievement.");
    }
    const newAch = {
      id: `ach-${Date.now()}`,
      title: achForm.title.trim(),
      category: achForm.category,
      year: achForm.year || String(new Date().getFullYear()),
      statNumber: achForm.statNumber.trim() || "Honor",
      statLabel: achForm.statLabel.trim() || "Award",
      description: achForm.description.trim(),
    };
    const updated = {
      ...portalContent,
      achievements: [newAch, ...portalContent.achievements],
    };
    handleSavePortalContent(updated);
    setAchForm({
      title: "",
      category: "STEM & Innovation",
      year: String(new Date().getFullYear()),
      statNumber: "",
      statLabel: "",
      description: "",
    });
    setShowAddAchievement(false);
  };

  const handleDeleteAchievement = (id) => {
    if (!window.confirm("Remove this achievement item?")) return;
    const updated = {
      ...portalContent,
      achievements: portalContent.achievements.filter((a) => a.id !== id),
    };
    handleSavePortalContent(updated);
  };

  // Event handlers
  const handleAddEvent = (e) => {
    e.preventDefault();
    if (!eventForm.title.trim() || !eventForm.summary.trim()) {
      return toast.warning("Please provide an event title and summary.");
    }
    const newEvent = {
      id: `evt-${Date.now()}`,
      title: eventForm.title.trim(),
      category: eventForm.category,
      date: eventForm.date,
      time: eventForm.time.trim() || "All Day",
      venue: eventForm.venue.trim() || "Main Campus",
      badge: eventForm.badge.trim() || "Event",
      summary: eventForm.summary.trim(),
    };
    const updated = {
      ...portalContent,
      events: [newEvent, ...portalContent.events],
    };
    handleSavePortalContent(updated);
    setEventForm({
      title: "",
      category: "Academic",
      date: new Date().toISOString().slice(0, 10),
      time: "09:00 AM – 02:00 PM",
      venue: "Main Campus Auditorium",
      badge: "Upcoming",
      summary: "",
    });
    setShowAddEvent(false);
  };

  const handleDeleteEvent = (id) => {
    if (!window.confirm("Remove this upcoming event/notice?")) return;
    const updated = {
      ...portalContent,
      events: portalContent.events.filter((e) => e.id !== id),
    };
    handleSavePortalContent(updated);
  };

  return (
    <main
      className={`min-h-full p-4 transition-colors sm:p-6 lg:p-8 ${
        isDarkMode ? "bg-slate-950 text-slate-100" : "bg-slate-50 text-slate-900"
      }`}
    >
      <Seo
        title="School Website Customizer | ClassNX Admin"
        description="Customize public homepage banners, school achievements, events, and public notices."
        canonical="/admin/home-slider"
        robots="noindex, follow"
      />

      <div className="mx-auto max-w-6xl space-y-6">
        {/* Header Hero */}
        <section className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-blue-800 via-indigo-800 to-cyan-700 p-7 text-white shadow-xl">
          <div className="pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full bg-cyan-400/20 blur-2xl" />

          <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
            <div>
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-white/15 px-3 py-1 text-xs font-bold uppercase tracking-wider text-cyan-200 backdrop-blur-md">
                  Public School Portal
                </span>
                <span className="text-xs text-blue-200">Tenant: {subdomain || "Default"}</span>
              </div>
              <h1 className="mt-3 text-2xl font-black sm:text-3xl">
                School Website & Homepage Customizer
              </h1>
              <p className="mt-2 max-w-2xl text-xs leading-relaxed text-blue-100 sm:text-sm">
                Control the public-facing homepage for <b>{schoolName}</b>. Manage image sliders, highlight academic and sports achievements, and publish upcoming school events.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2.5">
              <Link
                to="/"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-2.5 text-xs font-bold text-white backdrop-blur-md transition hover:bg-white/20"
              >
                <span>View Public Homepage</span>
                <FaExternalLinkAlt className="text-[10px]" />
              </Link>
              <Link
                to="/admin-board"
                className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2.5 text-xs font-bold text-slate-900 transition hover:bg-slate-100"
              >
                <FaArrowLeft />
                <span>Admin Board</span>
              </Link>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="mt-8 flex flex-wrap gap-2 border-t border-white/15 pt-4">
            <button
              type="button"
              onClick={() => setActiveTab("sliders")}
              className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-xs font-bold transition ${
                activeTab === "sliders"
                  ? "bg-white text-slate-900 shadow-md"
                  : "bg-white/10 text-white hover:bg-white/20"
              }`}
            >
              <FaImage />
              <span>Homepage Slider ({slides.length})</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab("achievements")}
              className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-xs font-bold transition ${
                activeTab === "achievements"
                  ? "bg-white text-slate-900 shadow-md"
                  : "bg-white/10 text-white hover:bg-white/20"
              }`}
            >
              <FaTrophy />
              <span>School Achievements ({portalContent.achievements.length})</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab("events")}
              className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-xs font-bold transition ${
                activeTab === "events"
                  ? "bg-white text-slate-900 shadow-md"
                  : "bg-white/10 text-white hover:bg-white/20"
              }`}
            >
              <FaCalendarAlt />
              <span>Upcoming Events & Notices ({portalContent.events.length})</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab("identity")}
              className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-xs font-bold transition ${
                activeTab === "identity"
                  ? "bg-white text-slate-900 shadow-md"
                  : "bg-white/10 text-white hover:bg-white/20"
              }`}
            >
              <FaGlobe />
              <span>School Information & Tagline</span>
            </button>
          </div>
        </section>

        {/* TAB 1: HOMEPAGE SLIDERS */}
        {activeTab === "sliders" && (
          <div className="space-y-6">
            <section
              className={`rounded-3xl border p-6 shadow-sm ${
                isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
              }`}
            >
              <h2 className="text-lg font-bold">Upload School Hero Banners</h2>
              <p className={`mt-1 text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                Add high-resolution landscape banners for your school's public homepage. Recommended resolution: 1600 × 500 px.
              </p>

              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-400">
                    Slide Headline (Optional)
                  </label>
                  <input
                    type="text"
                    value={slideTitle}
                    onChange={(e) => setSlideTitle(e.target.value)}
                    placeholder="e.g. Welcome to Academic Year 2026–27"
                    className={`mt-1.5 w-full rounded-2xl border px-3.5 py-2.5 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "border-slate-700 bg-slate-950 text-slate-100"
                        : "border-slate-300 bg-white text-slate-900"
                    }`}
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-400">
                    Slide Caption (Optional)
                  </label>
                  <input
                    type="text"
                    value={slideCaption}
                    onChange={(e) => setSlideCaption(e.target.value)}
                    placeholder="e.g. Empowering students with cutting-edge STEM and arts education."
                    className={`mt-1.5 w-full rounded-2xl border px-3.5 py-2.5 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "border-slate-700 bg-slate-950 text-slate-100"
                        : "border-slate-300 bg-white text-slate-900"
                    }`}
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="flex cursor-pointer flex-col items-center rounded-2xl border-2 border-dashed border-slate-300 p-8 text-center transition hover:border-blue-500 dark:border-slate-700">
                  <FaUpload className="text-3xl text-blue-600 dark:text-blue-400" />
                  <b className="mt-3 text-sm">Choose banner images</b>
                  <span className="mt-1 text-xs text-slate-500">
                    Up to 6 JPEG, PNG, or WebP files · 5 MB max per image
                  </span>
                  <input
                    className="sr-only"
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,image/webp"
                    onChange={chooseFiles}
                  />
                </label>
              </div>

              {files.length > 0 && (
                <div className="mt-4 flex items-center justify-between rounded-2xl bg-blue-50 p-4 dark:bg-blue-950/40">
                  <span className="text-xs font-bold text-blue-900 dark:text-blue-200">
                    {files.length} image{files.length > 1 ? "s" : ""} selected for upload
                  </span>
                  <button
                    type="button"
                    onClick={uploadSlides}
                    disabled={uploading}
                    className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-5 py-2 text-xs font-bold text-white shadow transition hover:bg-blue-700 disabled:opacity-50"
                  >
                    {uploading ? <FaSpinner className="animate-spin" /> : <FaUpload />}
                    <span>{uploading ? "Uploading..." : "Publish Banner Images"}</span>
                  </button>
                </div>
              )}
            </section>

            {/* Existing Slides Grid */}
            <section
              className={`rounded-3xl border p-6 shadow-sm ${
                isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold">Active Homepage Banners</h2>
                  <p className={`mt-0.5 text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                    These banners cycle automatically on your school's public homepage.
                  </p>
                </div>
                <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-bold text-blue-700 dark:bg-blue-950/60 dark:text-blue-300">
                  {slides.length} Published
                </span>
              </div>

              {slides.length === 0 ? (
                <div className="mt-6 rounded-2xl border border-dashed border-slate-300 p-10 text-center dark:border-slate-700">
                  <FaImage className="mx-auto text-3xl text-slate-400" />
                  <p className="mt-2 text-sm font-semibold">No custom banners published yet</p>
                  <p className="mt-1 text-xs text-slate-500">
                    Your public homepage currently displays the default branded school hero banner. Upload images above to enable the interactive slider!
                  </p>
                </div>
              ) : (
                <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {slides.map((slide, idx) => (
                    <article
                      key={slide.id || slide.slideId || idx}
                      className="group overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 shadow-sm transition hover:shadow-md dark:border-slate-800 dark:bg-slate-950"
                    >
                      <div className="relative aspect-[16/8] w-full overflow-hidden bg-slate-900">
                        <img
                          src={slide.imageUrl}
                          alt={slide.title || "Banner"}
                          className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
                        />
                        <span className="absolute left-2 top-2 rounded-md bg-slate-900/80 px-2 py-0.5 text-[10px] font-bold text-white backdrop-blur">
                          Slide #{idx + 1}
                        </span>
                      </div>

                      <div className="p-4">
                        <h3 className="truncate text-sm font-bold">{slide.title || "School Banner"}</h3>
                        {slide.caption && (
                          <p className="mt-1 line-clamp-2 text-xs text-slate-500">{slide.caption}</p>
                        )}
                        <div className="mt-4 flex items-center justify-between border-t border-slate-200 pt-3 dark:border-slate-800">
                          <span className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
                            ● Active on Public Page
                          </span>
                          <button
                            type="button"
                            onClick={() => removeSlide(slide)}
                            className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-bold text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40"
                          >
                            <FaTrash />
                            <span>Remove</span>
                          </button>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}

        {/* TAB 2: SCHOOL ACHIEVEMENTS */}
        {activeTab === "achievements" && (
          <div className="space-y-6">
            <section
              className={`rounded-3xl border p-6 shadow-sm ${
                isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
              }`}
            >
              <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <div>
                  <h2 className="text-lg font-bold">School Achievements & Honors</h2>
                  <p className={`mt-0.5 text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                    Highlight board exam merits, sports championships, Olympiad winners, and campus accolades.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowAddAchievement(!showAddAchievement)}
                  className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-5 py-2.5 text-xs font-bold text-white shadow transition hover:bg-amber-600"
                >
                  {showAddAchievement ? <FaTimes /> : <FaPlus />}
                  <span>{showAddAchievement ? "Close Form" : "Add New Achievement"}</span>
                </button>
              </div>

              {/* Add Achievement Form Modal / Expandable */}
              {showAddAchievement && (
                <form
                  onSubmit={handleAddAchievement}
                  className="mt-6 rounded-2xl border border-amber-200 bg-amber-50/50 p-5 dark:border-amber-900/40 dark:bg-amber-950/20"
                >
                  <h3 className="text-sm font-bold text-amber-900 dark:text-amber-200">
                    Add School Honor / Distinction
                  </h3>
                  <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 dark:text-slate-300">
                        Achievement Title *
                      </label>
                      <input
                        type="text"
                        required
                        value={achForm.title}
                        onChange={(e) => setAchForm({ ...achForm, title: e.target.value })}
                        placeholder="e.g. State Science Olympiad Gold"
                        className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs text-slate-900 focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 dark:text-slate-300">
                        Category
                      </label>
                      <select
                        value={achForm.category}
                        onChange={(e) => setAchForm({ ...achForm, category: e.target.value })}
                        className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs text-slate-900 focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                      >
                        {DEFAULT_PORTAL_CATEGORIES.map((cat) => (
                          <option key={cat} value={cat}>
                            {cat}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 dark:text-slate-300">
                        Year
                      </label>
                      <input
                        type="text"
                        value={achForm.year}
                        onChange={(e) => setAchForm({ ...achForm, year: e.target.value })}
                        placeholder="2025"
                        className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs text-slate-900 focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 dark:text-slate-300">
                        Metric Stat Highlight
                      </label>
                      <input
                        type="text"
                        value={achForm.statNumber}
                        onChange={(e) => setAchForm({ ...achForm, statNumber: e.target.value })}
                        placeholder="e.g. 1st Place or 98.4%"
                        className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs text-slate-900 focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 dark:text-slate-300">
                        Stat Label
                      </label>
                      <input
                        type="text"
                        value={achForm.statLabel}
                        onChange={(e) => setAchForm({ ...achForm, statLabel: e.target.value })}
                        placeholder="e.g. Gold Medal or Board Topper"
                        className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs text-slate-900 focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                      />
                    </div>

                    <div className="sm:col-span-2 lg:col-span-3">
                      <label className="block text-xs font-bold text-slate-700 dark:text-slate-300">
                        Description / Story *
                      </label>
                      <textarea
                        required
                        rows={2}
                        value={achForm.description}
                        onChange={(e) => setAchForm({ ...achForm, description: e.target.value })}
                        placeholder="Describe the student or school accomplishment..."
                        className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs text-slate-900 focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                      />
                    </div>
                  </div>

                  <div className="mt-4 flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setShowAddAchievement(false)}
                      className="rounded-full border border-slate-300 px-4 py-1.5 text-xs font-semibold"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="rounded-full bg-amber-500 px-5 py-1.5 text-xs font-bold text-white shadow hover:bg-amber-600"
                    >
                      Save Achievement
                    </button>
                  </div>
                </form>
              )}

              {/* Achievements List */}
              <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {portalContent.achievements.map((item) => (
                  <article
                    key={item.id}
                    className={`flex flex-col justify-between rounded-2xl border p-4 shadow-sm ${
                      isDarkMode ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"
                    }`}
                  >
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-[10px] font-bold text-amber-800 dark:bg-amber-950/60 dark:text-amber-300">
                          {item.category}
                        </span>
                        <span className="text-xs font-bold text-slate-400">{item.year}</span>
                      </div>
                      <h4 className="mt-2 text-sm font-bold">{item.title}</h4>
                      <p className="mt-1.5 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                        {item.description}
                      </p>
                    </div>

                    <div className="mt-4 flex items-center justify-between border-t border-slate-200 pt-3 dark:border-slate-800">
                      <div>
                        <b className="text-sm text-amber-600 dark:text-amber-400">{item.statNumber}</b>
                        <span className="ml-1 text-[11px] text-slate-400">{item.statLabel}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDeleteAchievement(item.id)}
                        className="rounded p-1.5 text-xs text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30"
                        title="Delete achievement"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          </div>
        )}

        {/* TAB 3: UPCOMING EVENTS & PUBLIC NOTICES */}
        {activeTab === "events" && (
          <div className="space-y-6">
            <section
              className={`rounded-3xl border p-6 shadow-sm ${
                isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
              }`}
            >
              <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <div>
                  <h2 className="text-lg font-bold">Upcoming Events & Public Notices</h2>
                  <p className={`mt-0.5 text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                    Keep prospective families, parents, and community members informed about campus events.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowAddEvent(!showAddEvent)}
                  className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-5 py-2.5 text-xs font-bold text-white shadow transition hover:bg-blue-700"
                >
                  {showAddEvent ? <FaTimes /> : <FaPlus />}
                  <span>{showAddEvent ? "Close Form" : "Add New Event / Notice"}</span>
                </button>
              </div>

              {/* Add Event Form */}
              {showAddEvent && (
                <form
                  onSubmit={handleAddEvent}
                  className="mt-6 rounded-2xl border border-blue-200 bg-blue-50/50 p-5 dark:border-blue-900/40 dark:bg-blue-950/20"
                >
                  <h3 className="text-sm font-bold text-blue-900 dark:text-blue-200">
                    Publish Public Event or Circular
                  </h3>
                  <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-bold text-slate-700 dark:text-slate-300">
                        Event / Circular Title *
                      </label>
                      <input
                        type="text"
                        required
                        value={eventForm.title}
                        onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                        placeholder="e.g. Annual Inter-School Sports Championship 2026"
                        className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs text-slate-900 focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 dark:text-slate-300">
                        Category
                      </label>
                      <select
                        value={eventForm.category}
                        onChange={(e) => setEventForm({ ...eventForm, category: e.target.value })}
                        className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs text-slate-900 focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                      >
                        {DEFAULT_PORTAL_CATEGORIES.map((cat) => (
                          <option key={cat} value={cat}>
                            {cat}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 dark:text-slate-300">
                        Event Date *
                      </label>
                      <input
                        type="date"
                        required
                        value={eventForm.date}
                        onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })}
                        className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs text-slate-900 focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 dark:text-slate-300">
                        Time / Hours
                      </label>
                      <input
                        type="text"
                        value={eventForm.time}
                        onChange={(e) => setEventForm({ ...eventForm, time: e.target.value })}
                        placeholder="e.g. 09:00 AM – 03:00 PM"
                        className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs text-slate-900 focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 dark:text-slate-300">
                        Venue
                      </label>
                      <input
                        type="text"
                        value={eventForm.venue}
                        onChange={(e) => setEventForm({ ...eventForm, venue: e.target.value })}
                        placeholder="e.g. Campus Sports Complex"
                        className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs text-slate-900 focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                      />
                    </div>

                    <div className="sm:col-span-2 lg:col-span-3">
                      <label className="block text-xs font-bold text-slate-700 dark:text-slate-300">
                        Summary Details *
                      </label>
                      <textarea
                        required
                        rows={2}
                        value={eventForm.summary}
                        onChange={(e) => setEventForm({ ...eventForm, summary: e.target.value })}
                        placeholder="Provide details about the event, registration information, or guidelines..."
                        className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs text-slate-900 focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                      />
                    </div>
                  </div>

                  <div className="mt-4 flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setShowAddEvent(false)}
                      className="rounded-full border border-slate-300 px-4 py-1.5 text-xs font-semibold"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="rounded-full bg-blue-600 px-5 py-1.5 text-xs font-bold text-white shadow hover:bg-blue-700"
                    >
                      Publish Event
                    </button>
                  </div>
                </form>
              )}

              {/* Events List */}
              <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {portalContent.events.map((evt) => (
                  <article
                    key={evt.id}
                    className={`flex flex-col justify-between rounded-2xl border p-4 shadow-sm ${
                      isDarkMode ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"
                    }`}
                  >
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-[10px] font-bold text-blue-800 dark:bg-blue-950/60 dark:text-blue-300">
                          {evt.badge || evt.category}
                        </span>
                        <span className="text-xs font-semibold text-slate-400">{evt.date}</span>
                      </div>
                      <h4 className="mt-2 text-sm font-bold">{evt.title}</h4>
                      <p className="mt-1 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                        {evt.summary}
                      </p>
                    </div>

                    <div className="mt-4 flex items-center justify-between border-t border-slate-200 pt-3 text-xs text-slate-400 dark:border-slate-800">
                      <span>{evt.venue || "Campus"}</span>
                      <button
                        type="button"
                        onClick={() => handleDeleteEvent(evt.id)}
                        className="rounded p-1.5 text-xs text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30"
                        title="Delete event"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          </div>
        )}

        {/* TAB 4: SCHOOL IDENTITY & CONTACT INFO */}
        {activeTab === "identity" && (
          <section
            className={`rounded-3xl border p-6 shadow-sm ${
              isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
            }`}
          >
            <h2 className="text-lg font-bold">School Identity & Contact Info</h2>
            <p className={`mt-0.5 text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
              Configure the public tagline, leadership overview, helpline numbers, and address displayed on the footer.
            </p>

            <div className="mt-6 grid gap-5 sm:grid-cols-2">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-400">
                  School Tagline / Motto
                </label>
                <input
                  type="text"
                  value={portalContent.tagline}
                  onChange={(e) =>
                    setPortalContent({ ...portalContent, tagline: e.target.value })
                  }
                  className={`mt-1.5 w-full rounded-2xl border px-3.5 py-2.5 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "border-slate-700 bg-slate-950 text-slate-100"
                      : "border-slate-300 bg-white text-slate-900"
                  }`}
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-400">
                  Curriculum & Affiliation
                </label>
                <input
                  type="text"
                  value={portalContent.affiliation}
                  onChange={(e) =>
                    setPortalContent({ ...portalContent, affiliation: e.target.value })
                  }
                  className={`mt-1.5 w-full rounded-2xl border px-3.5 py-2.5 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "border-slate-700 bg-slate-950 text-slate-100"
                      : "border-slate-300 bg-white text-slate-900"
                  }`}
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-400">
                  Welcome Overview Message
                </label>
                <textarea
                  rows={3}
                  value={portalContent.welcomeMessage}
                  onChange={(e) =>
                    setPortalContent({ ...portalContent, welcomeMessage: e.target.value })
                  }
                  className={`mt-1.5 w-full rounded-2xl border px-3.5 py-2.5 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "border-slate-700 bg-slate-950 text-slate-100"
                      : "border-slate-300 bg-white text-slate-900"
                  }`}
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-400">
                  Contact Inquiry Email
                </label>
                <input
                  type="email"
                  value={portalContent.contactEmail}
                  onChange={(e) =>
                    setPortalContent({ ...portalContent, contactEmail: e.target.value })
                  }
                  className={`mt-1.5 w-full rounded-2xl border px-3.5 py-2.5 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "border-slate-700 bg-slate-950 text-slate-100"
                      : "border-slate-300 bg-white text-slate-900"
                  }`}
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-400">
                  Admissions Helpline Phone
                </label>
                <input
                  type="text"
                  value={portalContent.contactPhone}
                  onChange={(e) =>
                    setPortalContent({ ...portalContent, contactPhone: e.target.value })
                  }
                  className={`mt-1.5 w-full rounded-2xl border px-3.5 py-2.5 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "border-slate-700 bg-slate-950 text-slate-100"
                      : "border-slate-300 bg-white text-slate-900"
                  }`}
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-400">
                  Campus Address
                </label>
                <input
                  type="text"
                  value={portalContent.campusAddress}
                  onChange={(e) =>
                    setPortalContent({ ...portalContent, campusAddress: e.target.value })
                  }
                  className={`mt-1.5 w-full rounded-2xl border px-3.5 py-2.5 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "border-slate-700 bg-slate-950 text-slate-100"
                      : "border-slate-300 bg-white text-slate-900"
                  }`}
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-400">
                  Office Working Hours
                </label>
                <input
                  type="text"
                  value={portalContent.officeHours}
                  onChange={(e) =>
                    setPortalContent({ ...portalContent, officeHours: e.target.value })
                  }
                  className={`mt-1.5 w-full rounded-2xl border px-3.5 py-2.5 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "border-slate-700 bg-slate-950 text-slate-100"
                      : "border-slate-300 bg-white text-slate-900"
                  }`}
                />
              </div>
            </div>

            <div className="mt-8 flex justify-end">
              <button
                type="button"
                onClick={() => handleSavePortalContent()}
                className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-6 py-2.5 text-xs font-bold text-white shadow transition hover:bg-blue-700"
              >
                <FaSave />
                <span>Save School Identity</span>
              </button>
            </div>
          </section>
        )}
      </div>
    </main>
  );
}
